import { useRef } from 'react'
import { sanitizeRichText } from '@/lib/sanitize'

interface EditableTextProps {
  value: string
  onCommit: (v: string) => void
  editable: boolean
  html?: boolean
  tag?: 'span' | 'div'
  className?: string
  style?: React.CSSProperties
  placeholder?: string
}

// Click-to-type text editing for the canvas. Commits on blur only (not per
// keystroke) — updateBlockProps() feeds the zundo undo stack, and per-keystroke
// commits would flood the 50-entry history and re-render the block on every key.
export function EditableText({ value, onCommit, editable, html = false, tag = 'span', className, style, placeholder }: EditableTextProps) {
  const ref = useRef<HTMLElement>(null)
  const Tag = tag

  if (!editable) {
    return html
      ? <Tag className={className} style={style} dangerouslySetInnerHTML={{ __html: sanitizeRichText(value) }} />
      : <Tag className={className} style={style}>{value || placeholder}</Tag>
  }

  const commit = () => {
    const el = ref.current
    if (!el) return
    const raw = html ? sanitizeRichText(el.innerHTML) : (el.innerText ?? '').trim()
    const next = raw === placeholder ? '' : raw
    if (next !== value) onCommit(next)
  }

  const handleFocus = (e: React.FocusEvent<HTMLElement>) => {
    if (value) return
    const el = e.currentTarget
    const range = document.createRange()
    range.selectNodeContents(el)
    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(range)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      const el = e.currentTarget
      if (html) el.innerHTML = sanitizeRichText(value)
      else el.innerText = value
      el.blur()
    }
  }

  const editableClassName = [
    className,
    'outline-none rounded cursor-text focus:ring-2 focus:ring-violet-400 focus:ring-offset-1',
    !value && !html ? 'opacity-50' : '',
  ].filter(Boolean).join(' ')

  const sharedProps = {
    ref: ref as React.Ref<HTMLDivElement>,
    contentEditable: true,
    suppressContentEditableWarning: true,
    onBlur: commit,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    className: editableClassName,
    style,
  } satisfies React.ComponentProps<'div'>

  if (html) {
    return <Tag {...sharedProps} dangerouslySetInnerHTML={{ __html: sanitizeRichText(value || placeholder || '') }} />
  }
  return <Tag {...sharedProps}>{value || placeholder}</Tag>
}
